import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import type { FinancePaymentStatus } from "@/api/agencyFinance";
import financeIcon from "@/assets/icons/finance-ecommerce.svg";
import closeIcon from "@/assets/icons/modal-close.svg";

export type PaymentOutcome =
	| { status: "completed"; reference: string }
	| { status: "on_hold"; reason: string }
	| { status: "rejected_by_finance"; reason: string }
	| { status: "in_process" };

interface Props {
	open: boolean;
	currentStatus: FinancePaymentStatus | "not_started";
	statementNumber: string | null;
	payeeLabel: string;
	campaignName: string;
	amountLabel: string;
	submitting: boolean;
	onCancel: () => void;
	onConfirm: (outcome: PaymentOutcome) => void;
}

type OutcomeKey = PaymentOutcome["status"];
const ALLOWED: Record<FinancePaymentStatus | "not_started", OutcomeKey[]> = {
	not_started: [],
	submitted: ["completed", "in_process", "on_hold", "rejected_by_finance"],
	in_review: ["completed", "in_process", "on_hold", "rejected_by_finance"],
	in_process: ["completed", "on_hold", "rejected_by_finance"],
	on_hold: ["in_process", "rejected_by_finance"],
	rejected_by_finance: ["in_process", "on_hold"],
	failed: [],
	completed: [],
};
const OPTIONS: Array<{
	key: OutcomeKey;
	label: string;
	hint: string;
	badge: string;
	tone: "paid" | "progress" | "action" | "danger";
}> = [
	{
		key: "completed",
		label: "Paid",
		hint: "Funds released to the creator's bank account. Closes the invoice.",
		badge: "Paid",
		tone: "paid",
	},
	{
		key: "in_process",
		label: "Scheduled for Payment",
		hint: "Approved and queued for the next payment run under 60-day terms.",
		badge: "In progress",
		tone: "progress",
	},
	{
		key: "on_hold",
		label: "On Hold",
		hint: "Pause processing while bank, tax or deliverable details are verified.",
		badge: "Needs action",
		tone: "action",
	},
	{
		key: "rejected_by_finance",
		label: "Declined",
		hint: "Decline the statement. The creator must correct it and resubmit.",
		badge: "Needs action",
		tone: "danger",
	},
];

export const PaymentOutcomeModal: React.FC<Props> = ({
	open,
	currentStatus,
	statementNumber,
	payeeLabel,
	campaignName,
	amountLabel,
	submitting,
	onCancel,
	onConfirm,
}) => {
	const [selected, setSelected] = useState<OutcomeKey | null>(null);
	const [reference, setReference] = useState("");
	const [reason, setReason] = useState("");
	const dialogRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		setSelected(null);
		setReference("");
		setReason("");
		const overflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		dialogRef.current?.focus();
		const escape = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !submitting) onCancel();
		};
		document.addEventListener("keydown", escape);
		return () => {
			document.body.style.overflow = overflow;
			document.removeEventListener("keydown", escape);
		};
	}, [open, onCancel, submitting]);

	if (!open) return null;
	const allowed = ALLOWED[currentStatus];
	const cleanReference = reference.trim();
	const cleanReason = reason.trim();
	const outcome: PaymentOutcome | null =
		selected === "completed" && cleanReference
			? { status: "completed", reference: cleanReference }
			: selected === "on_hold" && cleanReason
				? { status: "on_hold", reason: cleanReason }
				: selected === "rejected_by_finance" && cleanReason
					? { status: "rejected_by_finance", reason: cleanReason }
					: selected === "in_process"
						? { status: "in_process" }
						: null;
	const footerNote =
		selected === "rejected_by_finance"
			? "The statement returns to the creator for revision."
			: "The creator and campaign ledger are updated with the selected status.";

	return createPortal(
		<Overlay onMouseDown={() => !submitting && onCancel()}>
			<Dialog
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="payment-modal-title"
				tabIndex={-1}
				onMouseDown={(event) => event.stopPropagation()}
			>
				<Header>
					<TitleRow>
						<IconBadge>
							<img src={financeIcon} alt="" />
						</IconBadge>
						<TitleCopy>
							<h2 id="payment-modal-title">Acknowledge &amp; Process Payment</h2>
							<span>
								{[statementNumber, payeeLabel, campaignName, amountLabel].filter(Boolean).join("  ·  ")}
							</span>
						</TitleCopy>
						<Close type="button" aria-label="Close" disabled={submitting} onClick={onCancel}>
							<img src={closeIcon} alt="" />
						</Close>
					</TitleRow>
					<p>
						Record the outcome of this payment statement. The creator and the campaign ledger are updated
						with the status you select.
					</p>
				</Header>
				<Body>
					<SectionLabel>
						<strong id="payment-status-label">Set payment status</strong>
						<Required>Required</Required>
					</SectionLabel>
					<Options role="radiogroup" aria-labelledby="payment-status-label">
						{OPTIONS.map((option) => {
							const enabled = allowed.includes(option.key) && !submitting;
							const active = selected === option.key;
							return (
								<Option
									key={option.key}
									type="button"
									role="radio"
									aria-checked={active}
									disabled={!enabled}
									$active={active}
									$danger={option.tone === "danger"}
									onClick={() => setSelected(option.key)}
								>
									<Radio $active={active} $danger={option.tone === "danger"} />
									<OptionCopy>
										<strong>{option.label}</strong>
										<span>{option.hint}</span>
									</OptionCopy>
									<Badge $tone={option.tone}>{option.badge}</Badge>
								</Option>
							);
						})}
					</Options>
					{selected === "completed" && (
						<Field>
							<SectionLabel>
								<label htmlFor="payment-reference">Transaction reference</label>
								<Required>Required</Required>
							</SectionLabel>
							<Input
								id="payment-reference"
								autoFocus
								value={reference}
								onChange={(event) => setReference(event.target.value)}
								placeholder="e.g. AWX-20260919-0042"
							/>
						</Field>
					)}
					{(selected === "on_hold" || selected === "rejected_by_finance") && (
						<Field>
							<SectionLabel>
								<label htmlFor="payment-reason">
									{selected === "on_hold" ? "Reason for hold" : "Reason for rejection"}
								</label>
								<Required $danger={selected === "rejected_by_finance"}>Required</Required>
							</SectionLabel>
							<Textarea
								id="payment-reason"
								autoFocus
								rows={3}
								maxLength={500}
								value={reason}
								onChange={(event) => setReason(event.target.value)}
							/>
						</Field>
					)}
				</Body>
				<Footer>
					<FooterNote>{footerNote}</FooterNote>
					<Cancel type="button" disabled={submitting} onClick={onCancel}>
						Cancel
					</Cancel>
					<Confirm
						type="button"
						$danger={selected === "rejected_by_finance"}
						disabled={!outcome || submitting}
						onClick={() => outcome && onConfirm(outcome)}
					>
						{selected === "rejected_by_finance" ? "Decline" : submitting ? "Processing…" : "Confirm"}
					</Confirm>
				</Footer>
			</Dialog>
		</Overlay>,
		document.body,
	);
};

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 2200;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(21, 89, 165, 0.12);
	font-family: Poppins, sans-serif;
`;
const Dialog = styled.div`
	width: 640px;
	max-width: 100%;
	max-height: calc(100vh - 48px);
	overflow-y: auto;
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 12px 32px rgba(21, 89, 165, 0.24);
	outline: none;
`;
const Header = styled.header`
	padding: 28px 32px 20px;
	border-bottom: 1px solid #e6e6e6;
	> p {
		margin: 10px 0 0;
		color: #707070;
		font-size: 13px;
		line-height: 20px;
	}
`;
const TitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;
const IconBadge = styled.span`
	display: flex;
	flex: 0 0 40px;
	width: 40px;
	height: 40px;
	align-items: center;
	justify-content: center;
	border-radius: 12px;
	background: #e7e5ff;
	img {
		width: 22px;
		height: 22px;
	}
`;
const TitleCopy = styled.div`
	flex: 1;
	min-width: 0;
	h2 {
		margin: 0;
		color: #2a057f;
		font-size: 20px;
		font-weight: 600;
		line-height: 28px;
	}
	span {
		color: #707070;
		font-size: 13px;
		line-height: 20px;
	}
`;
const Close = styled.button`
	display: flex;
	width: 32px;
	height: 32px;
	align-items: center;
	justify-content: center;
	border: 0;
	border-radius: 50%;
	background: #f3f5ff;
	cursor: pointer;
	img {
		width: 16px;
		height: 16px;
	}
`;
const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 20px 32px 24px;
`;
const SectionLabel = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	color: #303030;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
`;
const Required = styled.span<{ $danger?: boolean }>`
	color: ${({ $danger }) => ($danger ? "#aa2222" : "#f72586")};
	font-size: 12px;
	font-weight: 500;
`;
const Options = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;
const Option = styled.button<{ $active: boolean; $danger: boolean }>`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	padding: 14px 16px;
	border-radius: 12px;
	border: ${({ $active, $danger }) => ($active && $danger ? "1.5px solid #aa2222" : "1px solid #e6e6e6")};
	background: ${({ $active, $danger }) => ($active && $danger ? "#fef4f8" : "#fff")};
	text-align: left;
	font: inherit;
	cursor: pointer;
	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;
const Radio = styled.span<{ $active: boolean; $danger: boolean }>`
	flex: 0 0 20px;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	box-sizing: border-box;
	background: #fff;
	border: ${({ $active, $danger }) =>
		$active ? `5.5px solid ${$danger ? "#aa2222" : "#3a0ca3"}` : "1.5px solid #b0b0b0"};
`;
const OptionCopy = styled.span`
	display: flex;
	flex: 1;
	min-width: 0;
	flex-direction: column;
	gap: 2px;
	strong {
		color: #303030;
		font-size: 14px;
		font-weight: 600;
		line-height: 20px;
	}
	span {
		color: #707070;
		font-size: 12px;
		line-height: 18px;
	}
`;
const TONES = {
	paid: ["#edf6e3", "#4d8a16"],
	progress: ["#e7ebfd", "#011992"],
	action: ["#fdf3df", "#a06e00"],
	danger: ["#fde2ef", "#c2004f"],
} as const;
const Badge = styled.span<{ $tone: keyof typeof TONES }>`
	flex: 0 0 auto;
	padding: 4px 10px;
	border-radius: 20px;
	background: ${({ $tone }) => TONES[$tone][0]};
	color: ${({ $tone }) => TONES[$tone][1]};
	font-size: 11px;
	font-weight: 600;
	line-height: 16px;
	white-space: nowrap;
`;
const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;
const Input = styled.input`
	height: 44px;
	padding: 0 14px;
	border: 1px solid #3a0ca3;
	border-radius: 12px;
	color: #303030;
	font:
		13px/20px Poppins,
		sans-serif;
	outline: none;
`;
const Textarea = styled.textarea`
	box-sizing: border-box;
	width: 100%;
	min-height: 72px;
	padding: 12px 14px;
	border: 1px solid #3a0ca3;
	border-radius: 12px;
	color: #303030;
	font:
		13px/20px Poppins,
		sans-serif;
	resize: vertical;
	outline: none;
`;
const Footer = styled.footer`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 20px 32px;
	border-top: 1px solid #e6e6e6;
	background: #fbfbff;
`;
const FooterNote = styled.span`
	flex: 1;
	color: #707070;
	font-size: 12px;
	line-height: 18px;
`;
const Cancel = styled.button`
	width: 112px;
	min-height: 40px;
	border: 2px solid #3a0ca3;
	border-radius: 64px;
	background: #fff;
	color: #3a0ca3;
	font:
		600 14px/24px Poppins,
		sans-serif;
	cursor: pointer;
`;
const Confirm = styled.button<{ $danger: boolean }>`
	width: 200px;
	min-height: 40px;
	border: 0;
	border-radius: 64px;
	color: #fff;
	background: ${({ $danger }) => ($danger ? "#bb2026" : "#3a0ca3")};
	font:
		600 14px/24px Poppins,
		sans-serif;
	cursor: pointer;
	&:disabled {
		background: #d9d9d9;
		color: #8c8c8c;
		cursor: not-allowed;
	}
`;
